<script setup>
import { i18n } from 'boot/i18n';
// App chrome, not app logic. Every settings page renders the same route-derived breadcrumb, and a
// page that dropped it would look like a different product. This is the context's ONLY import of an
// app component and it is deliberately shell-only — nothing about charges comes from outside.
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs.vue';
import {
  HrDataTable,
  HrPage,
  HrPageActionBtn,
  HrWarningBanner,
  notifyNegative,
  useDialog,
} from 'src/design-system';
import { computed, onMounted } from 'vue';

import { auth as useAuthStore } from 'src/store/auth';
import { formatCalendarDate } from 'src/utils/DateUtils';

import { currencyAffix } from '../../domain/constants/currency';
import { useChargeMaster } from '../composables/useChargeMaster';
import { useServiceCodes } from '../composables/useServiceCodes';
import { chargeLineCaption } from '../presenters/chargeLineCaption';
import { presentChargeLineRows } from '../presenters/chargeLineRowPresenter';
import EditChargeMasterDialog from '../components/EditChargeMasterDialog.vue';

/**
 * The clinic's charge master, read-only, with a deliberate action to manage it.
 *
 * READ AND EDIT ARE SPLIT ON PURPOSE. This page answers one question — "what do we charge now" — and
 * answers it without anything on screen that could change it. Versioning is the dialog's: which sheet
 * applies, scheduling the next one, correcting the current one. A page that did both would make
 * "look something up" and "change what we bill" the same surface.
 *
 * IT LIVES IN THE CONTEXT rather than among the app's settings pages, and the route resolves it
 * through this context's published interface. Assembling the charge master is Revenue Cycle's job;
 * publishing a composable, a presenter and a dialog so an outside file could assemble them would be a
 * wider interface than the one page it exists to serve.
 *
 * NOTHING IS SHOWN FOR A DATE WITH NO SHEET, and that is an answer rather than an empty state to
 * apologise for. Resolution never falls forward to a sheet that starts later, because charging a
 * February service at a March charge is the silent rewrite the whole pattern exists to prevent.
 */
defineOptions({ name: 'ChargeMasterPage' });

const chargeMaster = useChargeMaster();
const codes = useServiceCodes({ asOf: chargeMaster.asOf });
const { openComponentDialog } = useDialog();

const authStore = useAuthStore();

/** The tenant. Every read and write on this page is scoped by it. */
const clinicId = computed(() => authStore.userDetails?.clinic ?? null);

const load = async () => {
  await Promise.all([
    chargeMaster.load({ clinicId: clinicId.value }),
    codes.load({ clinicId: clinicId.value }),
  ]);
};

/**
 * A FAILED READ IS SAID OUT LOUD, because nothing else on this page would say it. Both composables
 * leave `hasLoaded` false when their read rejects, so the table renders its "no codes yet" label —
 * which is the one message that is certainly wrong here, and which invites somebody to add charges
 * that are already there. Offline, permission-denied and a session with no clinic all arrive this way.
 */
const loadAndReport = () => load().catch((error) => {
  notifyNegative(i18n.t('settings.billing.chargeMaster.notifyLoadFailed'));
  console.error('Failed to load the charge master', error);
});

onMounted(loadAndReport);

const rows = computed(() => presentChargeLineRows(
  chargeMaster.inForce.value,
  codes.codeById,
  { asOf: chargeMaster.asOf },
));

/** True once loaded, when no sheet applies today — including a clinic that has never made one. */
const hasNothingInForce = computed(() => (
  chargeMaster.hasLoaded.value
  && !chargeMaster.isAmbiguous.value
  && chargeMaster.inForce.value === null
));

/**
 * The day the next sheet starts, when nothing applies today but something is already scheduled.
 *
 * TWO EMPTY STATES, NOT ONE, and telling them apart is the whole point. A charge master may
 * legitimately start on a future date — a fee schedule beginning in January is entered in December,
 * which is why nothing caps it — so "no sheet applies today" covers both a clinic that has never made
 * one and a clinic whose sheet starts next month. Offering "Set up charge master" for the second told
 * the user nothing existed and then opened the sheet that did.
 *
 * Null whenever a sheet IS in force: a scheduled successor is the dialog's business, and naming it
 * here would answer a question this page does not ask.
 */
const scheduledStart = computed(() => (
  hasNothingInForce.value
    ? chargeMaster.scheduledEntries.value[0]?.effectiveFrom ?? null
    : null
));

/**
 * The four columns, in the order the design pins them.
 *
 * UNIT BASIS SITS IMMEDIATELY LEFT OF CHARGE and is never collapsed away, because a charge is not
 * legible without it: "120.00" says nothing, and per-15-min against per-hour is a fourfold
 * difference. Both are formatted here rather than in the presenter — the presenter returns tokens
 * and raw values so it stays testable without i18n, and turning those into words is the view's job.
 */
const columns = computed(() => [
  {
    name: 'code',
    label: i18n.t('settings.billing.chargeMaster.columnCode'),
    field: 'code',
    align: 'left',
    format: (code) => code ?? i18n.t('settings.billing.chargeMaster.textUnknownCode'),
  },
  /*
   * The descriptor cell carries the code's state when it has one — expiring, expired, or not yet
   * started. Without it the page showed a charge against a code that cannot be billed and looked
   * exactly like one that can, which is the row reading as more current than it is.
   */
  {
    name: 'descriptor',
    label: i18n.t('settings.billing.chargeMaster.columnDescriptor'),
    field: 'descriptor',
    align: 'left',
    format: (descriptor, row) => chargeLineCaption(row),
  },
  {
    name: 'unitBasis',
    label: i18n.t('settings.billing.chargeMaster.columnUnitBasis'),
    field: 'unitBasis',
    align: 'left',
    format: (unitBasis) => (unitBasis ? i18n.t(`unitBases.${unitBasis}`) : '—'),
  },
  /*
   * RIGHT-ALIGNED, and stated with its currency. Amounts are compared by their last digits, so a
   * column of them has to end in the same place — left-aligning money makes 0.50 and 500.00 look
   * like the same magnitude at a glance. The affix says what the number is denominated in, which a
   * bare figure leaves the reader to assume.
   */
  {
    name: 'charge',
    label: i18n.t('settings.billing.chargeMaster.columnCharge'),
    field: 'charge',
    align: 'right',
    format: (charge) => (charge === null
      ? i18n.t('settings.billing.chargeMaster.textNoCharge')
      : [currencyAffix(chargeMaster.inForce.value?.currency), charge].filter(Boolean).join(' ')),
  },
]);

/**
 * What an empty table says, which depends on WHY it is empty.
 *
 * THREE SITUATIONS, and one message would be wrong for the two it was not written for. Nothing in
 * force needs a charge master created; a sheet with no codes needs codes added to one that exists;
 * and a sheet that starts later needs neither, only the date it starts. The third reads as the first
 * unless it is asked about separately, which is what `scheduledStart` is for.
 */
const noDataLabel = computed(() => {
  if (scheduledStart.value !== null) {
    return i18n.t('settings.billing.chargeMaster.textScheduledNotInForce', {
      date: formatCalendarDate(scheduledStart.value),
    });
  }

  return hasNothingInForce.value
    ? i18n.t('settings.billing.chargeMaster.textNothingInForce')
    : i18n.t('settings.billing.chargeMaster.textNoLines');
});

const manage = () => openComponentDialog(
  EditChargeMasterDialog,
  { clinicId: clinicId.value },
  () => loadAndReport(),
);
</script>

<template>
  <hr-page>
    <!--
      `billing` IS NOT LINKED because it is not a page. It is a grouping in the settings menu and a
      segment in the path, and there is no route at `/settings/billing` for a link to reach — the same
      arrangement as `care-model` above Cohorts, which does the same thing for the same reason.
    -->
    <breadcrumbs :no-link="['billing']" />
    <p class="q-pb-md">
      {{ i18n.t('settings.billing.chargeMaster.textCaption') }}
    </p>

    <hr-warning-banner
      v-if="chargeMaster.isAmbiguous.value"
      :message="i18n.t('settings.billing.chargeMaster.warningAmbiguous')"
    />

    <!--
      THE TABLE IS ALWAYS RENDERED, even with nothing in force, because the action to fix that lives
      in its toolbar. Swapping the table for a banner when the sheet is empty would take the only
      route to creating one away at exactly the moment it is needed. The empty case is carried by the
      no-data label instead, which is what it is for.
    -->
    <hr-data-table
      v-else
      among-peers
      :rows="rows"
      :columns="columns"
      :loading="chargeMaster.isBusy.value"
      row-key="serviceCodeId"
      hide-pagination
      :rows-per-page-options="[0]"
      :no-data-label="noDataLabel"
      data-e2e="table-charge-master-read"
    >
      <template #actions>
        <!-- Set up only when there is genuinely nothing; a scheduled sheet is edited, not set up. -->
        <hr-page-action-btn
          :label="hasNothingInForce && scheduledStart === null
            ? i18n.t('settings.billing.chargeMaster.buttonSetUp')
            : i18n.t('settings.billing.chargeMaster.buttonManage')"
          data-e2e="button-manage-charge-master"
          @click="manage"
        />
      </template>
    </hr-data-table>
  </hr-page>
</template>
